import { classNames } from "helpers/ClassNameHelper";
import * as React from "react";

interface Props {
    checked: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    htmlFor: string;
    className?: string;
    value?: string;
    isDisabled?: boolean;
    valueNames?: Record<string, string>;
    text?: React.ReactNode;
    dropdownRef?: React.RefObject<HTMLElement>;
    onCloseDropdown?: () => void;
}

export const ToggleSwitch: React.FC<Props> = ({
    checked,
    isDisabled,
    className,
    onChange,
    htmlFor,
    value,
    valueNames,
    text,
    dropdownRef,
    onCloseDropdown,
}) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLLabelElement>) => {
        if (isDisabled) return;

        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            const syntheticEvent = {
                target: { checked: !checked },
            } as React.ChangeEvent<HTMLInputElement>;
            onChange(syntheticEvent);
        }

        if (e.key === "Tab" && dropdownRef?.current) {
            const focusable = Array.from(
                dropdownRef.current.querySelectorAll<HTMLElement>(
                    'input, button, select, textarea, [tabindex]:not([tabindex="-1"])'
                )
            ).filter(el => !el.hasAttribute("disabled"));

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (!e.shiftKey && document.activeElement === last) {
                onCloseDropdown?.();
            } else if (e.shiftKey && document.activeElement === first) {
                onCloseDropdown?.();
            }
        }
    };

    return (
        <label
            htmlFor={htmlFor}
            className={classNames(text ? "toggle-with-text" : "toggle", className)}
        >
            {valueNames && value && <span>{valueNames?.[value]}</span>}
            {text && <div className="toggle-with-text__label">{text}</div>}
            <input
                className={classNames("toggle__input", isDisabled && "toggle__input--disabled")}
                type="checkbox"
                value={value ?? ""}
                checked={checked}
                onChange={onChange}
                id={htmlFor}
                disabled={isDisabled}
            />
            <div
                className={classNames("toggle__fill", isDisabled && "toggle--disabled")}
                tabIndex={0}
                onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        const input = document.getElementById(htmlFor) as HTMLInputElement | null;
                        if (input && !isDisabled) {
                            input.click();
                        }
                    }
                }}
            >
                <div className="toggle__handle"></div>
            </div>
        </label>
    );
};
